/**
 * Theme: light, dark, or follow the system.
 *
 * Dark is the default. The app is used in studios and late-night curation
 * sessions where a bright screen is tiring, so a first launch opens dark; a
 * choice anyone has already made is kept. "system" remains available and is
 * genuinely live -- it tracks OS changes while the app is open, rather than only
 * reading the preference at startup.
 *
 * The choice is written to <html data-theme>, which the CSS treats as an explicit
 * override of prefers-color-scheme. In "system" mode the attribute is removed
 * entirely so the media query takes over again.
 */

import { useCallback, useEffect, useState } from "react";

export type ThemeChoice = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "nova.theme";

function readStored(): ThemeChoice {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // Private mode or blocked storage: fall through to the default.
  }
  return "dark";
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches === true
  );
}

export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(readStored);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Track OS changes live, so "system" keeps meaning something after startup.
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolved: ResolvedTheme =
    choice === "system" ? (systemDark ? "dark" : "light") : choice;

  useEffect(() => {
    const root = document.documentElement;
    if (choice === "system") {
      // Remove the attribute so prefers-color-scheme governs again.
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", choice);
    }
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // Preference simply will not persist; the UI still works this session.
    }
  }, [choice]);

  const cycle = useCallback(() => {
    setChoice((c) => (c === "light" ? "dark" : c === "dark" ? "system" : "light"));
  }, []);

  return { choice, resolved, setChoice, cycle };
}
